import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AppTableRow } from '../../shared/app-table/app-table-row.interface';
import { SecurityService } from '../security.service';
import { GetLiveSecurityFindingsResponseDto } from './models/getLiveSecurityFindingsResponseDto';
import { Globals } from 'src/app/shared/utils/globals';
import { LiveSecurityFindingDto } from './models/liveSecurityFindingDto';
import { FindingDetailDialogComponent } from '../../apps/app-data/app-close-issues/finding-detail-dialog/finding-detail-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { AppSelectOption } from '../../shared/app-select/app-select-option.interface';
import { FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-open-production-findings',
  templateUrl: './open-production-findings.component.html',
  styleUrls: ['./open-production-findings.component.scss'],
})
export class OpenProductionFindingsComponent implements OnInit {
  @Input() liveSecurityFindingsResponse: GetLiveSecurityFindingsResponseDto;
  @Input() appTableRows: AppTableRow[];
  @Output() securityFindingDialogClosed = new EventEmitter<boolean>();
  openingSecurityFindingModal: string;
  platformAppSelectOptions: AppSelectOption[] = [];
  severityAppSelectOptions: AppSelectOption[] = [];
  displayingLiveSecurityFindings: LiveSecurityFindingDto[];
  securityFindingsFilterForm = new FormGroup({
    text: new FormControl<string>(''),
    platform: new FormControl<string>(''),
    severity: new FormControl<string>(''),
  });
  filtersEnabled: boolean;
  readonly excludedTableColumns = new Set([
    'severity',
    'appPlatform',
    'actions',
    'openingDate',
  ]);

  constructor(
    private globals: Globals,
    private securityService: SecurityService,
    private dialog: MatDialog,
    private translateService: TranslateService,
  ) {}

  ngOnInit() {
    this.displayingLiveSecurityFindings =
      this.liveSecurityFindingsResponse?.liveSecurityFindings;
    this.defineAppTableRows();
    this.definePlatformAppSelectOptions();
    this.defineSeverityAppSelectOptions();
    this.initializeSecurityFindingFiltering();
  }

  initializeSecurityFindingFiltering() {
    this.securityFindingsFilterForm.valueChanges.subscribe((formValue) => {
      this.displayingLiveSecurityFindings =
        this.liveSecurityFindingsResponse.liveSecurityFindings.filter(
          (x) =>
            (x.appName
              .toLowerCase()
              .includes(formValue.text?.toLowerCase() ?? '') ||
              x.title
                .toLowerCase()
                .includes(formValue.text?.toLowerCase() ?? '')) &&
            x.appPlatform
              .toLowerCase()
              .includes(formValue.platform?.toLowerCase() ?? '') &&
            x.severity
              .toLowerCase()
              .includes(formValue.severity?.toLowerCase() ?? ''),
        );
    });
  }

  defineAppTableRows() {
    const tableRows = this.translateService.instant(
      'component.open-production-findings.app-table.table-rows',
    );
    this.appTableRows = Object.entries(tableRows).map(
      ([key, value]) =>
        ({ displayName: value, columnName: key } as AppTableRow),
    );
  }

  definePlatformAppSelectOptions() {
    const platforms = this.translateService.instant(
      'component.open-production-findings.app-table-filters.platform-select.options',
    );
    this.platformAppSelectOptions = Object.entries(platforms).map(
      ([key, value]) => ({ displayName: value, value: key } as AppSelectOption),
    );
  }

  defineSeverityAppSelectOptions() {
    const severities = this.translateService.instant(
      'component.open-production-findings.app-table-filters.severity-select.options',
    );
    this.severityAppSelectOptions = Object.entries(severities).map(
      ([key, value]) => ({ displayName: value, value: key } as AppSelectOption),
    );
  }

  defineAppPlatformImageSource(appPlatform: string) {
    return `assets/images/assets/${
      appPlatform === this.globals.tech_iOS ? 'ios' : 'android'
    }.png`;
  }

  getSeverityDotClasses(severity: string) {
    return {
      'severity-dot-critical': severity === 'CRITICAL',
      'severity-dot-high': severity === 'HIGH',
      'severity-dot-medium': severity === 'MEDIUM',
      'severity-dot-low': severity === 'LOW',
    };
  }

  isDefaultColumn(columnName: string) {
    return !this.excludedTableColumns.has(columnName);
  }

  openSecurityFindingsDetailDialog(
    liveSecurityFinding: LiveSecurityFindingDto,
  ) {
    this.openingSecurityFindingModal = liveSecurityFinding.id;
    this.securityService
      .getSecurityFindingsDetail(
        liveSecurityFinding.appId,
        liveSecurityFinding.id,
      )
      .subscribe({
        next: (res) => {
          const data = {
            technology: liveSecurityFinding.appPlatform,
            appUuid: liveSecurityFinding.appId,
            findingId: liveSecurityFinding.id,
            result: res,
          };
          this.handleCloseFindingDialog(data);
        },
      });
  }

  handleCloseFindingDialog(data: any) {
    const closeFindingDialog = this.dialog.open(FindingDetailDialogComponent, {
      width: '760px',
      minHeight: '600px',
      panelClass: 'clsFindingDetailDialog',
      data: data,
    });

    closeFindingDialog
      .afterOpened()
      .subscribe(() => (this.openingSecurityFindingModal = null));

    closeFindingDialog.afterClosed().subscribe((res) => {
      if (res) {
        this.securityFindingDialogClosed.emit();
      }
    });
  }

  clearFilters() {
    this.securityFindingsFilterForm.reset();
  }

  toggleFiltersEnabled() {
    this.filtersEnabled = !this.filtersEnabled;
    if (!this.filtersEnabled) {
      this.clearFilters();
    }
  }

  defineCloseFindingTooltipImage(findingId: string) {
    return this.openingSecurityFindingModal === findingId
      ? 'assets/images/icons/reload_big_pink.gif'
      : 'assets/images/icons/icons-close-on.svg';
  }
}
